import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Container,
  Paper,
  Fade,
  Slide,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Security as SecurityIcon,
  AccountCircle as AccountIcon,
  Logout,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Turnstile from "react-turnstile";
import { LoaderWrapper } from './index';
import { parseEncryptedCredentials, encryptText } from '../utils/encryption';

const isProd = process.env.REACT_APP_NODE_ENV === 'production';
const schema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
  captcha: isProd ? yup.string().required('Captcha verification is required') : yup.string().notRequired(),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin, getRedirectUrl, isAuthenticated, loading } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [newTab, setNewTab] = React.useState(false);
  const [error, setError] = React.useState('');
  const [mounted, setMounted] = React.useState(false);
  const [cloudToken, setCloudToken] = React.useState(null);

  // Initialize form hooks before any conditional rendering
  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: '',
      password: '',
      captcha: '',
    }
  });

  React.useEffect(() => {
    const token = localStorage.getItem("authToken");

    if (token) {
      setNewTab(true)
      setError('You Are Already Logged in')
    }
  }, []);

  const handleLogout = () => {
  localStorage.clear();

  // Trigger logout event for other tabs
  localStorage.setItem("logout", Date.now());

  // Redirect current tab
   window.location.reload()
};

  React.useEffect(() => {
    setMounted(true);

    // Check for type=S parameter and set userTypeId to 4
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('type') === 'S') {
      localStorage.setItem('userTypeId', 4);
      console.log('✅ Set userTypeId to 4 due to type=S parameter');
    }

    // Check for encrypted credentials in URL
    if (location.search) {
      console.log('🔍 Checking URL for encrypted parameters...');
      console.log('Current URL:', window.location.href);

      const decryptedCredentials = parseEncryptedCredentials(location.search);

      if (decryptedCredentials) {
        console.log('✅ Successfully decrypted credentials from URL!');
        console.log('Username:', decryptedCredentials.username);
        console.log('Token', decryptedCredentials.token);

        if (decryptedCredentials.token === 'NABLNEWTOKENCHECKQWERTYUIBGFS') {
          const filldata = {
            username: decryptedCredentials.username,
            captcha: '', // Captcha must be completed manually
          }

          // Handle async operation properly
          const handleAutoLogin = async () => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login-auto`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(filldata),
              });

              // Convert response to JSON
              const data = await response.json();

              if (data.success) {
                const {
                  username,
                  orgId,
                  appId,
                  token,
                  labTypeId,
                  userTypeId,
                  redirectTo,
                  passwordChangeRequired,
                  isSubUser,
                  mainOrgId,
                  disciplineId,
                  disciplineIds,
                  subUserOrgId,
                } = data.data;

                // For sub-users, store main org's orgId and appId
                const effectiveOrgId = isSubUser && mainOrgId ? mainOrgId : orgId;
                const effectiveAppId = appId || effectiveOrgId;

                // Store authentication data in localStorage
                localStorage.setItem('orgId', effectiveOrgId);
                localStorage.setItem('authToken', token);
                localStorage.setItem('username', username);
                localStorage.setItem('appId', effectiveAppId);
                localStorage.setItem('labTypeId', labTypeId ? labTypeId.toString() : '');
                // localStorage.setItem('userTypeId', userTypeId ? userTypeId.toString() : '');

                // Store sub-user specific data
                if (isSubUser) {
                  localStorage.setItem('isSubUser', 'true');
                  localStorage.setItem('mainOrgId', mainOrgId?.toString() || '');
                  localStorage.setItem('mainAppId', effectiveAppId?.toString() || '');
                  localStorage.setItem('subUserOrgId', subUserOrgId?.toString() || '');
                  if (disciplineId) {
                    localStorage.setItem('disciplineId', disciplineId?.toString() || '');
                  }
                  // Store multiple discipline IDs for subuser
                  if (disciplineIds && Array.isArray(disciplineIds)) {
                    localStorage.setItem('disciplineIds', JSON.stringify(disciplineIds));
                  }

                } else {
                  // Clear sub-user flags for regular users
                  localStorage.removeItem('isSubUser');
                  localStorage.removeItem('mainOrgId');
                  localStorage.removeItem('mainAppId');
                  localStorage.removeItem('subUserOrgId');
                  localStorage.removeItem('disciplineId');
                  localStorage.removeItem('disciplineIds');
                }

                // Store complete user data
                const userData = {
                  username,
                  orgId: effectiveOrgId,
                  appId: effectiveAppId,
                  labTypeId,
                  userTypeId,
                  isSubUser: isSubUser || false,
                  mainOrgId: mainOrgId || null,
                  subUserOrgId: subUserOrgId || null,
                  disciplineId: disciplineId || null,
                  loginTime: new Date().toISOString(),
                  redirectTo: redirectTo || '/dashboard'
                };
                localStorage.setItem('userData', JSON.stringify(userData));

                //   toast.success("Login successful!");

                // Log the stored data for verification
                console.log("Stored authentication data:", {
                  username,
                  orgId: effectiveOrgId,
                  appId: effectiveAppId,
                  labTypeId,
                  userTypeId,
                  isSubUser,
                  loginSuccessful: true,
                });

                // Handle password change requirement
                if (passwordChangeRequired) {
                  toast.info("Password change required");
                  navigate("/main/change-password", { state: { username } });
                } else {
                  // Redirect based on server response or fallback
                  let redirectUrl = redirectTo || getRedirectUrl();

                  // Add encrypted query parameters for subusers accessing discipline pages
                  if (isSubUser && (redirectUrl.includes('/discipline') || redirectUrl.includes('/medical/') || redirectUrl.includes('/testing/') || redirectUrl.includes('/calibration/'))) {
                    const encryptedOrgId = encryptText(effectiveOrgId.toString());
                    const encryptedAppId = encryptText(effectiveAppId.toString());
                    const separator = redirectUrl.includes('?') ? '&' : '?';
                    redirectUrl = `${redirectUrl}${separator}orgId=${encodeURIComponent(encryptedOrgId)}&appId=${encodeURIComponent(encryptedAppId)}`;
                    console.log('🔗 Added encrypted query params for subuser:', { redirectUrl, orgId: effectiveOrgId, appId: effectiveAppId });
                  }

                  navigate(`/main${redirectUrl}`);
                }
              } else {
                // Handle login failure
                if (response.status === 403 && data?.isBlocked) {
                  // Account is blocked
                  toast.error(data?.message || "Your account is blocked due to inactivity last 7 days. Please create new application or contact NABL for assistance.", {
                    duration: 8000,
                    style: {
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                    },
                  });
                } else {
                  toast.error(data.message || "Login failed");
                }
              }
            } catch (error) {
              console.error("Auto-login error:", error);
              toast.error("Something went wrong during auto-login");
            }
          };

          handleAutoLogin();

          // Auto-fill the form with decrypted credentials
          setValue('username', decryptedCredentials.username);
          setValue('password', decryptedCredentials.password);

          // Show success message
          // toast.success('Credentials auto-filled from encrypted URL parameters');

        } else {
          console.log('⚠️ Token is not "changeme", manual login required');
        }


      } else {
        console.log('ℹ️ No encrypted credentials found in URL or decryption failed');
      }
    }
  }, [location.search, setValue, getRedirectUrl, navigate]);


  // Show loading screen while checking authentication
  if (loading) {
    return (
      <LoaderWrapper
        loading={true}
        type="fullscreen"
        message="Checking authentication..."
      />
    );
  }

  const onSubmit = async (data) => {
    if (isProd) {
      console.log('🔒 Production mode: validating captcha');
      if (!cloudToken) {
        setError('Please complete the captcha verification');
        toast.error('Please complete the captcha verification');
        return;
      }
    } else {
      setError('');
    }

    setLoginLoading(true);
    setError('');

    try {
      const loginData = {
        username: data.username,
        password: data.password,
        captchaToken: cloudToken || '',
      };

      const response = await authLogin(loginData);

      if (response.success) {
        const {
          username,
          orgId,
          appId,
          labTypeId,
          redirectTo,
          passwordChangeRequired,
          userTypeId,
          isSubUser,
          mainOrgId,
          subUserOrgId,
          disciplineId,
          labTypeIds,
          disciplineIds
        } = response.data;

        // Store userTypeId (if available)
        if (userTypeId) {
          localStorage.setItem('userTypeId', userTypeId);
        }
        if (labTypeIds) {
          localStorage.setItem('labTypeIds', labTypeIds);
        }

        // Store discipline information for subusers
        if (isSubUser) {
          localStorage.setItem('isSubUser', 'true');

          // Store single discipline ID for backward compatibility
          if (disciplineId) {
            localStorage.setItem('disciplineId', disciplineId);
          }

          // Store multiple discipline IDs for subuser
          if (disciplineIds && Array.isArray(disciplineIds)) {
            localStorage.setItem('disciplineIds', JSON.stringify(disciplineIds));
            console.log('Stored disciplineIds for subuser:', disciplineIds);
          }

          // Store organization IDs for subuser
          if (mainOrgId) {
            localStorage.setItem('mainOrgId', mainOrgId);
          }
          if (subUserOrgId) {
            localStorage.setItem('subUserOrgId', subUserOrgId);
          }
        } else {
          // Clean up subuser-specific data for regular users
          localStorage.removeItem('isSubUser');
          localStorage.removeItem('disciplineId');
          localStorage.removeItem('disciplineIds');
          localStorage.removeItem('mainOrgId');
          localStorage.removeItem('subUserOrgId');
        }

        // Store complete user data
        const userData = {
          username,
          orgId,
          appId,
          labTypeId,
          userTypeId,
          isSubUser: isSubUser || false,
          mainOrgId: mainOrgId || null,
          subUserOrgId: subUserOrgId || null,
          disciplineId: disciplineId || null,
          loginTime: new Date().toISOString(),
          redirectTo: redirectTo || '/dashboard'
        };
        localStorage.setItem('userData', JSON.stringify(userData));

        // toast.success('Login successful!');

        // Log the stored data for verification
        console.log('Stored authentication data:', {
          username,
          orgId,
          appId,
          labTypeId,
          userTypeId,
          isSubUser,
          loginSuccessful: true
        });

        // Handle password change requirement
        if (passwordChangeRequired) {
          toast.info('Password change required');
          navigate('/main/change-password', { state: { username } });
        } else {
          // Redirect based on server response or use the redirect URL from auth service
          let redirectUrl = redirectTo || getRedirectUrl();

          // Add encrypted query parameters for subusers accessing discipline pages
          if (isSubUser && (redirectUrl.includes('/discipline') || redirectUrl.includes('/medical/') || redirectUrl.includes('/testing/') || redirectUrl.includes('/calibration/'))) {
            const encryptedOrgId = encryptText(orgId.toString());
            const encryptedAppId = encryptText(appId.toString());
            const separator = redirectUrl.includes('?') ? '&' : '?';
            redirectUrl = `${redirectUrl}${separator}orgId=${encodeURIComponent(encryptedOrgId)}&appId=${encodeURIComponent(encryptedAppId)}`;
            console.log('🔗 Added encrypted query params for subuser:', { redirectUrl, orgId, appId });
          }

          navigate(`/main${redirectUrl}`);
        }
      } else {
        setError('Login failed');
        toast.error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);

      // Handle different types of errors
      let errorMessage = 'Login failed. Please try again.';
      let isAccountBlocked = false;
      let blockInfo = null;

      if (error.response) {
        // HTTP error response from server
        const status = error.response.status;
        const responseData = error.response.data;

        if (status === 403 && responseData?.isBlocked) {
          // Account is blocked due to expired application
          isAccountBlocked = true;
          blockInfo = responseData;
          errorMessage = responseData?.message || 'Your account is blocked due to inactivity last 7 days. Please create new application or contact NABL for assistance.';
        } else if (status === 401) {
          // Authentication failed - wrong username or password
          errorMessage = responseData?.message || 'Invalid username or password';
        } else if (status === 404) {
          // Endpoint not found - this shouldn't happen for login
          errorMessage = 'Invalid username or password';
        } else if (status >= 500) {
          // Server error
          errorMessage = 'Server error. Please try again later.';
        } else {
          // Other HTTP errors
          errorMessage = responseData?.message || `Error ${status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Network error - request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred.';
      }

      setError(errorMessage);

      // Show appropriate toast message
      if (isAccountBlocked) {
        toast.error(errorMessage, {
          duration: 8000, // Show longer for blocked accounts
          style: {
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#dc2626',
          },
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          animation: 'float 20s ease-in-out infinite',
        },
      }}
    >
      {newTab === false ? (
        <Container maxWidth="sm">
          <Fade in={mounted} timeout={1000}>
            <Paper
              elevation={24}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transform: mounted ? 'translateY(0)' : 'translateY(50px)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.35)',
                },
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                  color: 'white',
                  p: 4,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #06b6d4)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s ease-in-out infinite',
                  },
                }}
              >
                <Slide direction="down" in={mounted} timeout={800}>
                  <Box>
                    <SecurityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight="bold"
                      sx={{
                        mb: 1,
                        background: 'linear-gradient(45deg, #ffffff, #e0f2fe)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      }}
                    >
                      NABL Portal
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      National Accreditation Board for Testing and Calibration Laboratories
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Slide direction="up" in={mounted} timeout={1000}>
                  <Box>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography
                        variant="h5"
                        component="h2"
                        fontWeight="600"
                        color="primary"
                        gutterBottom
                      >
                        Welcome Back
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please sign in to access your dashboard
                      </Typography>
                    </Box>

                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            fontSize: '1.2rem',
                          },
                        }}
                      >
                        {error}
                      </Alert>
                    )}
                    {/* <Alert
                    severity="info"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        fontSize: '1.2rem',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                      If you are an existing lab and want to apply under renewal category, Please register and make “Do you have accreditation login on the NABL old portal? ” as yes and proceed.
                    </Typography>
                  </Alert> */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Box sx={{ mb: 3 }}>
                        <Controller
                          name="username"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Username"
                              variant="outlined"
                              error={!!errors.username}
                              helperText={errors.username?.message}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  },
                                  '&.Mui-focused': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                  },
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AccountIcon sx={{ color: '#1e3c72' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ mb: 4 }}>
                        <Controller
                          name="password"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Password"
                              type={showPassword ? 'text' : 'password'}
                              variant="outlined"
                              error={!!errors.password}
                              helperText={errors.password?.message}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  },
                                  '&.Mui-focused': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(30, 60, 114, 0.3)',
                                  },
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LockIcon sx={{ color: '#1e3c72' }} />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowPassword}
                                      edge="end"
                                      sx={{
                                        color: '#1e3c72',
                                        '&:hover': {
                                          backgroundColor: '#e3f2fd',
                                          transform: 'scale(1.1)',
                                        },
                                      }}
                                    >
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Turnstile
                          sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                          onVerify={(token) => {
                            setCloudToken(token);
                            setValue('captcha', token);
                          }}
                          onExpire={() => {
                            setCloudToken(null);
                            setValue('captcha', '');
                          }}
                          onError={() => {
                            setCloudToken(null);
                            setValue('captcha', '');
                          }}
                        />
                        {errors.captcha && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1, textAlign: 'center' }}
                          >
                            {errors.captcha.message}
                          </Typography>
                        )}
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<LoginIcon />}
                        disabled={
                          isProd
                            ? !cloudToken || loginLoading
                            : loginLoading
                        }
                        sx={{
                          mb: 3,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                          boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                            transition: 'left 0.5s ease',
                          },
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                            '&::before': {
                              left: '100%',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                            transform: 'none',
                          },
                        }}
                      >
                        <LoaderWrapper loading={loginLoading} type="button">
                          {loginLoading ? 'Signing In...' : 'Sign In to Portal'}
                        </LoaderWrapper>
                      </Button>
                    </form>

                    <Divider sx={{ my: 3, '&::before, &::after': { borderColor: 'rgba(0,0,0,0.1)' } }}>
                      <Typography variant="body2" color="text.secondary">
                        Need Help?
                      </Typography>
                    </Divider>

                    <Grid container spacing={2} justifyContent="center">
                      <Grid item xs={12} sm={4}>
                        <Link
                          component={RouterLink}
                          to="/main/forgot-password"
                          variant="body2"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            p: 1,
                            borderRadius: 1,
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            color: '#1e3c72',
                            '&:hover': {
                              backgroundColor: '#e3f2fd',
                              color: '#1e3c72',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          Forgot Password?
                        </Link>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Link
                          component={RouterLink}
                          to="/main/register"
                          variant="body2"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            p: 1,
                            borderRadius: 1,
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            color: '#3b82f6',
                            '&:hover': {
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          Create Account
                        </Link>
                      </Grid>
                      {/* <Grid item xs={12} sm={4}>
                        <a
                          href="https://nablwpnew.qci.org.in/Home/login"
                          style={{
                            display: 'block',
                            fontSize: '0.87rem',
                            textAlign: 'center',
                            padding: '10px 16px',
                            borderRadius: '4px',
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            color: '#3b82f6',
                            '&:hover': {
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              transform: 'translateY(-1px)',
                            },
                          }}
                        >
                          Login for Other
                        </a>
                      </Grid> */}
                    </Grid>
                  </Box>
                </Slide>
              </CardContent>
            </Paper>
          </Fade>
        </Container>
      ): (
         <Container maxWidth="sm">
          <Fade in={mounted} timeout={1000}>
            <Paper
              elevation={24}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transform: mounted ? 'translateY(0)' : 'translateY(50px)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.35)',
                },
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                  color: 'white',
                  p: 4,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #06b6d4)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s ease-in-out infinite',
                  },
                }}
              >
                <Slide direction="down" in={mounted} timeout={800}>
                  <Box>
                    <SecurityIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight="bold"
                      sx={{
                        mb: 1,
                        background: 'linear-gradient(45deg, #ffffff, #e0f2fe)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      }}
                    >
                      NABL Portal
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      National Accreditation Board for Testing and Calibration Laboratories
                    </Typography>
                  </Box>
                </Slide>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Slide direction="up" in={mounted} timeout={1000}>
                  <Box>

                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            fontSize: '1.2rem',
                          },
                        }}
                      >
                        {error}
                      </Alert>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={<LoginIcon />}
                        onClick={handleLogout}
                        sx={{
                          mb: 3,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                          boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                            transition: 'left 0.5s ease',
                          },
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                            '&::before': {
                              left: '100%',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                            transform: 'none',
                          },
                        }}
                      >
                        <LoaderWrapper  type="button">
                         Logout Now
                        </LoaderWrapper>
                      </Button>
                  </Box>
                </Slide>
              </CardContent>
            </Paper>
          </Fade>
        </Container>
      )}


      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Box>
  );
};

export default LoginPage;
